import React, { useState, useEffect, useRef } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Document, Packer, Paragraph, Table, TableCell, TableRow } from "docx";
// import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
// import { jsPDF } from "jspdf";
import "jspdf-autotable";
// import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
// import TableChartIcon from "@mui/icons-material/TableChart";
import { useDispatch } from "react-redux";
import { setFilteredReports } from "../../../../../store/slices/filteredDataSlice";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";
import { ChartColumnDecreasing } from "lucide-react"
import { TableOfContents } from "lucide-react"
import { Download } from "lucide-react"
import pdfMake from "pdfmake/build/pdfmake.min";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;


const Sales = ({ showgraph, filteredReports = [], isDownloadViewOnly = false }) => {
    const popupRef = useRef(null);
    const currency = useCurrencySymbol();
    const dispatch = useDispatch();
    const USAComma = CommaConvert();

    const [topLeads, setTopLeads] = useState([]); // New state for top 10 leads
    const [groupFilteredReports, setGroupFilteredReports] = useState([]);

    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [probabilityTab, setProbabilityTab] = useState("graph");
    const [isDropdownVisibleRevenue, setIsDropdownVisibleRevenue] =
        useState(false);
    const [probabilityTabRevenue, setProbabilityTabRevenue] = useState("graph");
    const [groupedData, setGroupedData] = useState([]);

    useEffect(() => {
        if (filteredReports && Array.isArray(filteredReports)) {

            const wonLeads = filteredReports.filter(lead =>
                lead.Status === "Won" || lead.Status === "Won Recurring"
            );
            setGroupFilteredReports(wonLeads);
        }
    }, [filteredReports]);

    useEffect(() => {
        dispatch(setFilteredReports());
    }, [dispatch]);

    useEffect(() => {
        if (groupFilteredReports && Array.isArray(groupFilteredReports)) {
            const top10SortedLeads = [...groupFilteredReports]
                .filter((lead) => typeof lead.QuotedValue === "number")
                .sort((a, b) => b.QuotedValue - a.QuotedValue)
                .slice(0, 10)
                .sort((a, b) => a.QuotedValue - b.QuotedValue);

            setTopLeads(top10SortedLeads);
        }
    }, [groupFilteredReports]);

    useEffect(() => {
        const groupedObject = topLeads.reduce((acc, lead) => {
            const type = lead.TypeService || "";
            if (!acc[type]) {
                acc[type] = {
                    TypeService: type,
                    leads: [],
                    totalQuotedValue: 0,
                };
            }
            acc[type].leads.push(lead);
            acc[type].totalQuotedValue += Number(lead.QuotedValue || 0);
            return acc;
        }, {});
        const groupedArray = Object.values(groupedObject);
        setGroupedData(groupedArray);
    }, [topLeads, showgraph]);

    const handleClickOutside = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setIsDropdownVisibleRevenue(false); // Close the popup if clicked outside
        }
    };
    useEffect(() => {
        if (isDropdownVisibleRevenue) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isDropdownVisibleRevenue]);

    const dropdownRefclient = useRef(null);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRefclient.current &&
                !dropdownRefclient.current.contains(event.target)
            ) {
                setIsDropdownVisible(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    const toggleDropdown = () => {
        if (isDownloadViewOnly) return;
        setIsDropdownVisible(!isDropdownVisible);
    };
    const handleDownload = (event) => {
        if (isDownloadViewOnly) return;
        const value = event.target.value;

        if (value === "pdf") {
            downloadPDF();
        } else if (value === "word") {
            downloadWord();
        } else if (value === "excel") {
            downloadExcel();
        }
        setIsDropdownVisible(false);
    };

    const headerColor = "#0e7490"

    const downloadPDF = () => {

        const now = new Date();
        const day = now.getDate();
        const month = now.toLocaleString("en-US", { month: "short" });
        const year = now.getFullYear();
        const fileName = `ClientLeadReports_${day}_${month}_${year}.pdf`;

        // 👉 Instead of topLeads from jsPDF, we use tableLeadReports here
        const totalQuotedValue = topLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue || 0),
            0
        );

        const formattedTotal = `${USAComma(totalQuotedValue)}`;

        // ------------ HEADER ROW ------------
        const headerRow = [
            { text: "Sr.No", style: "tableHeader", fillColor: headerColor, color: "white" },
            { text: "Client Name", style: "tableHeader", fillColor: headerColor, color: "white" },
            { text: `Quoted Value (${currency})`, style: "tableHeader", fillColor: headerColor, color: "white" }
        ];

        // ------------ BODY ROWS ------------
        const bodyRows = topLeads.map((LeadReport, index) => {
            const isEvenRow = index % 2 === 0;
            const bgColor = isEvenRow ? "#ffffff" : "#f3f4f6";

            return [
                { text: index + 1, fillColor: bgColor, style: "tableBody" },
                { text: LeadReport.ClientName, fillColor: bgColor, style: "tableBody" },
                { text: `${USAComma(LeadReport.QuotedValue)}`, fillColor: bgColor, style: "tableBody" }
            ];
        });

        // ------------ FOOTER ROW (TOTAL) ------------
        const footerRow = [
            { text: " ", style: "tableBody", fillColor: "#e5e7eb" },
            { text: "Total", bold: true, fillColor: "#e5e7eb", style: "tableBody" },
            { text: formattedTotal, bold: true, fillColor: "#e5e7eb", style: "tableBody" }
        ];

        // ------------ PDF DOCUMENT DEFINITION ------------
        const docDefinition = {
            pageSize: "A4",
            pageOrientation: "portrait",

            content: [
                {
                    text: "ClientLeadReports",
                    style: "title",
                    margin: [0, 0, 0, 10]
                },
                {
                    table: {
                        headerRows: 1,
                        widths: [40, "*", 80],
                        body: [
                            headerRow,
                            ...bodyRows,
                            footerRow        // 👈 Add total row like jsPDF
                        ]
                    },
                    layout: "lightHorizontalLines"
                }
            ],

            styles: {
                title: { fontSize: 12, bold: true },
                tableHeader: { bold: true, fontSize: 10 },
                tableBody: { fontSize: 9 }
            }
        };

        pdfMake.createPdf(docDefinition).download(fileName);
    };

    const downloadWord = () => {
        const headerRow = new TableRow({
            children: [
                new TableCell({ children: [new Paragraph("Sr.No")] }),

                new TableCell({ children: [new Paragraph("Client Name")] }),
                new TableCell({ children: [new Paragraph(`Quoted Value${currency}`)] }),
            ],
        });

        const dataRows = topLeads.map((LeadReport, index) => {
            return new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph(`${index + 1}`)] }),
                    new TableCell({ children: [new Paragraph(LeadReport.ClientName)] }),
                    new TableCell({
                        children: [
                            new Paragraph(
                                ` ${USAComma(LeadReport.QuotedValue)}`
                            ),
                        ],
                    }),
                ],
            });
        });

        // 🔢 Calculate total quoted value
        const totalQuotedValue = topLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue),
            0
        );
        const formattedTotal = ` ${USAComma(totalQuotedValue)}`;

        // ➕ Footer Row
        const footerRow = new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph("")],
                    verticalAlign: "center",
                }),
                new TableCell({
                    children: [new Paragraph("Total")],
                    verticalAlign: "center",
                }),
                new TableCell({
                    children: [new Paragraph(formattedTotal)],
                    verticalAlign: "center",
                }),
            ],
        });

        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        new Table({
                            rows: [headerRow, ...dataRows, footerRow],
                        }),
                    ],
                },
            ],
        });

        Packer.toBlob(doc).then((blob) => {
            saveAs(blob, "ClientLeadReports.docx");
        });
    };

    const downloadExcel = () => {
        const sortedLeads = [...topLeads].sort(
            (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
        );

        const quotedValueKey = `Quoted Value (${currency})`;

        // Create data rows
        const data = sortedLeads.map((LeadReport, index) => ({
            "Sr.No": index + 1,
            "Client Name": LeadReport.ClientName,
            [quotedValueKey]: `${USAComma(Number(LeadReport.QuotedValue))}`,
        }));

        // Calculate total
        const totalQuotedValue = sortedLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue),
            0
        );

        // Add total row
        data.push({
            "Sr.No": "",
            "Client Name": "Total",
            [quotedValueKey]: `${USAComma(totalQuotedValue)}`,
        });

        // Generate worksheet and workbook
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

        // Save as Excel file
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            bookType: "xlsx",
            type: "application/octet-stream",
        });
        saveAs(excelFile, "ClientLeadReports.xlsx");
    };


    // const AllClient = () => {
    //     const sortedLeads = [...groupFilteredReports].sort(
    //         (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
    //     );

    //     // Create data rows
    //     const data = sortedLeads.map((LeadReport, index) => ({
    //         "Sr.No": index + 1,
    //         "Client Name": LeadReport.ClientName,
    //         "Quoted Value": Number(LeadReport.QuotedValue), // Keep as number
    //     }));

    //     // ➕ Calculate total
    //     const totalQuotedValue = sortedLeads
    //         .reduce((sum, lead) => sum + Number(lead.QuotedValue), 0);

    //     // Add a total row at the bottom
    //     data.push({
    //         "Sr.No": "",
    //         "Client Name": "Total",
    //         "Quoted Value": USAComma(totalQuotedValue),
    //     });

    //     // Generate worksheet and workbook
    //     const ws = XLSX.utils.json_to_sheet(data);
    //     const wb = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

    //     // Save as Excel file
    //     const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    //     const excelFile = new Blob([excelBuffer], {
    //         bookType: "xlsx",
    //         type: "application/octet-stream",
    //     });
    //     saveAs(excelFile, "LeadReports.xlsx");
    // };

    const AllClient = () => {
        if (isDownloadViewOnly) return;
        const sortedLeads = [...groupFilteredReports].sort(
            (a, b) => new Date(a.LeadDate) - new Date(b.LeadDate)
        );

        const quotedValueKey = `Quoted Value (${currency})`;

        // Create data rows
        const data = sortedLeads.map((LeadReport, index) => ({
            "Sr.No": index + 1,
            "Client Name": LeadReport.ClientName,
            [quotedValueKey]: `${USAComma(Number(LeadReport.QuotedValue))}`, // formatted
        }));

        // Calculate total
        const totalQuotedValue = sortedLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue),
            0
        );

        // Add total row
        data.push({
            "Sr.No": "",
            "Client Name": "Total",
            [quotedValueKey]: `${USAComma(totalQuotedValue)}`, // same key
        });

        // Generate worksheet and workbook
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

        // Save as Excel file
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            bookType: "xlsx",
            type: "application/octet-stream",
        });
        saveAs(excelFile, "AllClientList.xlsx");
    };

    const AllClientServiceType = () => {
        if (isDownloadViewOnly) return;
        if (!groupFilteredReports || !Array.isArray(groupFilteredReports)) return;

        // Prepare data with only required columns
        const data = groupFilteredReports.map((lead) => ({
            TypeService: lead.TypeService?.join(", ") || "", // join array into string
            [`QuotedValue (${currency})`]: `${USAComma(Number(lead.QuotedValue))}`,
            // QuotedValue: lead.QuotedValue || "",
        }));

        // Convert to worksheet
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

        // Generate Excel file and trigger download
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "ClientServicesList.xlsx");
    };
    //for Revenue//
    const toggleDropdownRevenue = () => {
        if (isDownloadViewOnly) return;
        setIsDropdownVisibleRevenue(!isDropdownVisibleRevenue);
    };

    const handleDownloadRevenue = (event) => {
        if (isDownloadViewOnly) return;
        const value = event.target.value;

        if (value === "pdf") {
            downloadPDFRevenue();
        } else if (value === "word") {
            downloadWordRevenue();
        } else if (value === "excel") {
            downloadExcelRevenue();
        }
        setIsDropdownVisibleRevenue(false);
    };

    const downloadPDFRevenue = () => {
        // 🔥 FIX: Deep clone your data (avoids read-only mutation error)
        const safeGroupedData = JSON.parse(JSON.stringify(groupedData));

        const now = new Date();
        const day = now.getDate();
        const month = now.toLocaleString("en-US", { month: "short" });
        const year = now.getFullYear();
        const fileName = `RevenueLeadReport_${day}_${month}_${year}.pdf`;

        const totalQuotedValue = safeGroupedData.reduce(
            (sum, lead) => sum + Number(lead.totalQuotedValue || 0),
            0
        );
        const formattedTotal = `${USAComma(totalQuotedValue)}`;

        // ---- HEADER (no styles here)
        const headerRow = [
            { text: "Sr.No", style: "tableHeader" },
            { text: "Service Type", style: "tableHeader" },
            { text: `Quoted Value (${currency})`, style: "tableHeader" }
        ];

        // ---- BODY
        const bodyRows = safeGroupedData.map((lead, index) => {
            const isEven = index % 2 === 0;
            const bg = isEven ? "#ffffff" : "#f3f4f6";

            return [
                { text: index + 1, fillColor: bg, style: "tableBody" },
                { text: lead.TypeService.join(", "), fillColor: bg, style: "tableBody" },
                { text: `${USAComma(lead.totalQuotedValue)}`, fillColor: bg, style: "tableBody" }
            ];
        });

        // ---- FOOTER
        const footerRow = [
            { text: " ", fillColor: "#e5e7eb", style: "tableBody" },
            { text: "Total", bold: true, fillColor: "#e5e7eb", style: "tableBody" },
            { text: formattedTotal, bold: true, fillColor: "#e5e7eb", style: "tableBody" }
        ];

        const docDefinition = {
            pageSize: "A4",
            pageOrientation: "portrait",

            content: [
                {
                    text: "Revenue Lead Report",
                    style: "title",
                    margin: [0, 0, 0, 10]
                },
                {
                    table: {
                        headerRows: 1,
                        widths: [40, "*", 80],
                        body: [headerRow, ...bodyRows, footerRow]
                    },
                    layout: {
                        fillColor: (rowIndex) => {
                            if (rowIndex === 0) return headerColor; // header row bgcolor
                            return null;
                        },
                        hLineColor: () => "#e5e7eb",
                        vLineColor: () => "#e5e7eb"
                    }
                }
            ],

            styles: {
                title: { fontSize: 12, bold: true },
                tableHeader: { bold: true, fontSize: 10, color: "white" },
                tableBody: { fontSize: 9 }
            }
        };

        pdfMake.createPdf(docDefinition).download(fileName);
    };


    const downloadWordRevenue = () => {
        const headerRow = new TableRow({
            children: [
                new TableCell({ children: [new Paragraph("S.No")] }),

                new TableCell({ children: [new Paragraph("Service Type")] }),
                new TableCell({ children: [new Paragraph(`Quoted Value (${currency})`)] }),
            ],
        });

        const dataRows = groupedData.map((LeadReport, index) => {
            return new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph(`${index + 1}`)] }),
                    new TableCell({ children: [new Paragraph(LeadReport.TypeService.join(", "))] }),
                    new TableCell({
                        children: [
                            new Paragraph(
                                `${USAComma(LeadReport.totalQuotedValue)}`
                            ),
                        ],
                    }),
                ],
            });
        });

        // 🔢 Calculate total quoted value
        const totalQuotedValue = groupedData
            .reduce((sum, lead) => sum + Number(lead.totalQuotedValue), 0);
        const formattedTotal = `${USAComma(totalQuotedValue)}`;

        const footerRow = new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph(" ")],
                    verticalAlign: "center",
                }),
                new TableCell({
                    children: [new Paragraph("Total")],
                    verticalAlign: "center",
                }),
                new TableCell({
                    children: [new Paragraph(formattedTotal)],
                    verticalAlign: "center",
                }),
            ],
        });

        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        new Table({
                            rows: [headerRow, ...dataRows, footerRow],
                        }),
                    ],
                },
            ],
        });

        Packer.toBlob(doc).then((blob) => {
            saveAs(blob, "RevenueLeadReport.docx");
        });
    };
    // const downloadExcelRevenue = () => {

    //     const data = topLeads.map((LeadReport, index) => ({
    //         "Sr. No": index + 1,
    //         "Service Type": LeadReport.TypeService?.join(", ") || "",
    //         [`Quoted Value (${currency})`]: Number(LeadReport.QuotedValue),
    //     }));

    //     // ➕ Calculate total
    //     const totalQuotedValue = topLeads
    //         .reduce((sum, lead) => sum + Number(lead.QuotedValue), 0);

    //     data.push({
    //         "Sr.No": "",
    //         "Service Type": "Total",
    //         "Quoted Value": USAComma(totalQuotedValue),
    //     });

    //     // Generate worksheet and workbook
    //     const ws = XLSX.utils.json_to_sheet(data);
    //     const wb = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

    //     // Save as Excel file
    //     const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    //     const excelFile = new Blob([excelBuffer], {
    //         bookType: "xlsx",
    //         type: "application/octet-stream",
    //     });
    //     saveAs(excelFile, "RevenueLeadReport.xlsx");
    // };

    const downloadExcelRevenue = () => {
        const quotedValueKey = `Quoted Value (${currency})`; // ✅ dynamic key

        // Create data rows
        const data = topLeads.map((LeadReport, index) => ({
            "Sr. No": index + 1,
            "Service Type": LeadReport.TypeService?.join(", ") || "",
            [quotedValueKey]: `${USAComma(Number(LeadReport.QuotedValue))}`,
            // [quotedValueKey]: Number(LeadReport.QuotedValue),
        }));

        // ➕ Calculate total
        const totalQuotedValue = topLeads.reduce(
            (sum, lead) => sum + Number(lead.QuotedValue),
            0
        );

        // Add total row
        data.push({
            "Sr. No": "",
            "Service Type": "Total",
            [quotedValueKey]: `${USAComma(Number(totalQuotedValue))}`, // ✅ use the same key
            // [quotedValueKey]: USAComma(totalQuotedValue), // ✅ use the same key
        });

        // Generate worksheet and workbook
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Lead Reports");

        // Save as Excel file
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const excelFile = new Blob([excelBuffer], {
            bookType: "xlsx",
            type: "application/octet-stream",
        });
        saveAs(excelFile, "RevenueLeadReport.xlsx");
    };


    // Rank-based donut colors (shared mapping) — matches provided reference.
    // Rank 1 → Dark green, Rank 2 → Light green, Rank 3 → Strong blue,
    // Rank 4 → Medium blue, Rank 5–10 → Light blue gradient.
    const COLORS = [
        "#1F7A3A",
        "#8DB84A",
        "#1A73E8",
        "#4A9ED9",
        "#74B5E8",
        "#9EC7F6",
        "#B8DCF5",
        "#CCE7F8",
        "#DDF0FA",
        "#EEF7FD",
    ];
    const COLORSrevenue = COLORS;

    const sortedTopLeads = topLeads
        .slice()
        .sort((a, b) => (b.QuotedValue || 0) - (a.QuotedValue || 0));
    
    // Use adaptive transformation: keep large values at 0.98, compress smaller values more
    // This ensures the largest value stays perfect while smaller values are more compressed
    const maxValue = Math.max(...sortedTopLeads.map(lead => lead.QuotedValue || 0));
    const data = sortedTopLeads.map((lead, index) => {
        const originalValue = lead.QuotedValue || 0;
        if (originalValue <= 0) {
            return {
            name: lead.ClientName || `Lead ${index + 1}`,
                value: 0,
                originalValue: originalValue,
                originalIndex: index,
            };
        }
        
        // Calculate relative size (0 to 1)
        const relativeSize = originalValue / maxValue;
        
        // Use a proportional curve that preserves value differences clearly for ALL values
        // This ensures: smaller values have smaller bars, larger values have larger bars
        // All values show proportional differences according to their amounts
        // Very small (₹90,000): power 0.4-0.6 (preserves differences, still small)
        // Small-medium (₹1,00,000): power 0.6-0.8 (preserves differences, small)
        // Medium (₹3,63,000, ₹5,00,000, ₹8,00,000): power 0.8-0.95 (clear differences)
        // Large-medium (₹9,00,000, ₹10,00,000): power 0.95-0.98 (almost no compression, much bigger)
        // Largest: power 0.98 (no compression)
        let power;
        if (relativeSize > 0.08) {
            // Largest values (₹9,90,099, ₹10,00,000): power 0.999-1.0 (almost no compression - dominates chart)
            power = 0.999 + ((relativeSize - 0.08) / 0.92) * 0.001; // 0.999 to 1.0
        } else if (relativeSize > 0.05) {
            // Large-medium values: power 0.99-0.999 (₹8,00,000+ range - very large)
            power = 0.99 + ((relativeSize - 0.05) / 0.03) * 0.009; // 0.99 to 0.999
        } else if (relativeSize > 0.03) {
            // Medium values: power 0.9-0.99 (₹3,63,000, ₹5,00,000 range - clearly visible and large)
            power = 0.9 + ((relativeSize - 0.03) / 0.02) * 0.09; // 0.9 to 0.99
        } else if (relativeSize > 0.01) {
            // Small-medium values: power 0.4-0.9 (₹1,00,000 range - visible but much smaller)
            power = 0.4 + ((relativeSize - 0.01) / 0.02) * 0.5; // 0.4 to 0.9
        } else {
            // Very small values (₹90,000): power 0.08-0.4 (heavily compressed - tiny but visible)
            power = 0.08 + (relativeSize / 0.01) * 0.32; // 0.08 to 0.4
        }
        const transformedValue = Math.pow(originalValue, power);
        
        return {
            name: lead.ClientName || `Lead ${index + 1}`,
            value: transformedValue,
            originalValue: originalValue, // Keep original for tooltip
            originalIndex: index, // Store the index to find the exact lead
        };
    });
    
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const payloadData = payload[0].payload || payload[0];
            const dataIndex = payloadData.originalIndex;
            
            // Get the exact lead using the dataIndex from the sorted array
            const exactLead = dataIndex !== undefined && sortedTopLeads[dataIndex] ? sortedTopLeads[dataIndex] : null;
            
            if (!exactLead) {
                // Fallback: try to find by name and value
            const { name, value } = payload[0];
                const fallbackLead = sortedTopLeads.find(lead => lead.ClientName === name && lead.QuotedValue === value);
                if (!fallbackLead) return null;
                
                const colorIndex = sortedTopLeads.findIndex(l => l === fallbackLead);
                const bgColor = COLORS[colorIndex % COLORS.length] || "#1d72f3";
                
            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        padding: "8px",
                        borderRadius: "6px",
                        boxShadow: "0px 0px 8px rgba(0,0,0,0.1)",
                            maxWidth: "300px",
                    }}
                >
                        <p style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "13px" }}>
                        <strong>{name}</strong>
                    </p>
                        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "6px" }}>
                            <div 
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    backgroundColor: `${bgColor}15`,
                                    borderLeft: `3px solid ${bgColor}`,
                                }}
                            >
                                <span style={{ fontSize: "12px", color: "#374151" }}>
                                    {fallbackLead.ClientName}
                                </span>
                                <span style={{ fontSize: "12px", fontWeight: "600", color: "#111827" }}>
                                    {currency}{USAComma(fallbackLead.QuotedValue)}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            }
            
            const { name } = payload[0];
            const colorIndex = dataIndex;
            const bgColor = COLORS[colorIndex % COLORS.length] || "#1d72f3";
            
            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        padding: "8px",
                        borderRadius: "6px",
                        boxShadow: "0px 0px 8px rgba(0,0,0,0.1)",
                        maxWidth: "300px",
                    }}
                >
                    <p style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "13px" }}>
                        <strong>{name}</strong>
                    </p>
                    <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "6px" }}>
                        <div 
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                backgroundColor: `${bgColor}15`,
                                borderLeft: `3px solid ${bgColor}`,
                            }}
                        >
                            <span style={{ fontSize: "12px", color: "#374151" }}>
                                {exactLead.ClientName}
                            </span>
                            <span style={{ fontSize: "12px", fontWeight: "600", color: "#111827" }}>
                                {currency}{USAComma(exactLead.QuotedValue)}
                            </span>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

 

    // const datarevenue = groupedData
    const sortedTopLeadsRevenue = topLeads
        .slice()
        .sort((a, b) => (b.QuotedValue || 0) - (a.QuotedValue || 0));
    
    // Use adaptive transformation: keep large values at 0.98, compress smaller values more
    // This ensures the largest value stays perfect while smaller values are more compressed
    const maxValueRevenue = Math.max(...sortedTopLeadsRevenue.map(lead => lead.QuotedValue || 0));
    const datarevenue = sortedTopLeadsRevenue.map((lead, index) => {
        const serviceTypes = lead.TypeService || [];
        const displayName = serviceTypes.length > 1 ? "Multiple" : (serviceTypes[0] || "");
        const originalValue = lead.QuotedValue || 0;
        
        if (originalValue <= 0) {
            return {
                name: displayName,
                value: 0,
                originalValue: originalValue,
                originalIndex: index,
                originalServiceTypes: serviceTypes,
            };
        }
        
        // Calculate relative size (0 to 1)
        const relativeSize = originalValue / maxValueRevenue;
        
        // Use a proportional curve that preserves value differences clearly for ALL values
        // This ensures: smaller values have smaller bars, larger values have larger bars
        // All values show proportional differences according to their amounts
        // Very small (₹90,000): power 0.4-0.6 (preserves differences, still small)
        // Small-medium (₹1,00,000): power 0.6-0.8 (preserves differences, small)
        // Medium (₹3,63,000, ₹5,00,000, ₹8,00,000): power 0.8-0.95 (clear differences)
        // Large-medium (₹9,00,000, ₹10,00,000): power 0.95-0.98 (almost no compression, much bigger)
        // Largest: power 0.98 (no compression)
        let power;
        if (relativeSize > 0.08) {
            // Largest values (₹9,90,099, ₹10,00,000): power 0.999-1.0 (almost no compression - dominates chart)
            power = 0.999 + ((relativeSize - 0.08) / 0.92) * 0.001; // 0.999 to 1.0
        } else if (relativeSize > 0.05) {
            // Large-medium values: power 0.99-0.999 (₹8,00,000+ range - very large)
            power = 0.99 + ((relativeSize - 0.05) / 0.03) * 0.009; // 0.99 to 0.999
        } else if (relativeSize > 0.03) {
            // Medium values: power 0.9-0.99 (₹3,63,000, ₹5,00,000 range - clearly visible and large)
            power = 0.9 + ((relativeSize - 0.03) / 0.02) * 0.09; // 0.9 to 0.99
        } else if (relativeSize > 0.01) {
            // Small-medium values: power 0.4-0.9 (₹1,00,000 range - visible but much smaller)
            power = 0.4 + ((relativeSize - 0.01) / 0.02) * 0.5; // 0.4 to 0.9
        } else {
            // Very small values (₹90,000): power 0.08-0.4 (heavily compressed - tiny but visible)
            power = 0.08 + (relativeSize / 0.01) * 0.32; // 0.08 to 0.4
        }
        const transformedValue = Math.pow(originalValue, power);
        
        return {
            name: displayName,
            value: transformedValue,
            originalValue: originalValue, // Keep original for tooltip
            originalIndex: index, // Store the index to find the exact lead
            originalServiceTypes: serviceTypes, // Store original service types
        };
    });
        

    const CustomTooltiprevenue = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const payloadData = payload[0].payload || payload[0];
            const dataIndex = payloadData.originalIndex;
            
            // Get the exact lead using the dataIndex from the sorted array
            const exactLead = dataIndex !== undefined && sortedTopLeadsRevenue[dataIndex] ? sortedTopLeadsRevenue[dataIndex] : null;
            
            if (!exactLead) {
                // Fallback: try to find by name and value
            const { name, value } = payload[0];
                const serviceTypesToMatch = Array.isArray(name) ? name : [name];
                const isMultiple = serviceTypesToMatch.length > 1;
                const displayName = isMultiple ? "Multiple" : (serviceTypesToMatch[0] || "");
                
                const fallbackLead = sortedTopLeadsRevenue.find(lead => {
                    const leadServiceTypes = lead.TypeService || [];
                    const leadIsMultiple = leadServiceTypes.length > 1;
                    const leadDisplayName = leadIsMultiple ? "Multiple" : (leadServiceTypes[0] || "");
                    return leadDisplayName === displayName && lead.QuotedValue === value;
                });
                
                if (!fallbackLead) return null;
                
                const serviceTypes = fallbackLead.TypeService || [];
                const displayServiceType = serviceTypes.length > 1 ? "Multiple" : (serviceTypes[0] || "");
                const colorIndex = sortedTopLeadsRevenue.findIndex(l => l === fallbackLead);
                const bgColor = COLORSrevenue[colorIndex % COLORSrevenue.length] || "#1d72f3";
                
            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        padding: "8px",
                        borderRadius: "6px",
                        boxShadow: "0px 0px 8px rgba(0,0,0,0.1)",
                            maxWidth: "300px",
                    }}
                >
                        <p style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "13px" }}>
                            <strong>{displayServiceType}</strong>
                        </p>
                        <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "6px" }}>
                            <div 
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    backgroundColor: `${bgColor}15`,
                                    borderLeft: `3px solid ${bgColor}`,
                                }}
                            >
                                <span style={{ fontSize: "12px", color: "#374151" }}>
                                    {displayServiceType}
                                </span>
                                <span style={{ fontSize: "12px", fontWeight: "600", color: "#111827" }}>
                                    {currency}{USAComma(fallbackLead.QuotedValue)}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            }
            
            const serviceTypes = exactLead.TypeService || [];
            const displayServiceType = serviceTypes.length > 1 ? "Multiple" : (serviceTypes[0] || "");
            const colorIndex = dataIndex;
            const bgColor = COLORSrevenue[colorIndex % COLORSrevenue.length] || "#1d72f3";
            
            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ccc",
                        padding: "8px",
                        borderRadius: "6px",
                        boxShadow: "0px 0px 8px rgba(0,0,0,0.1)",
                        maxWidth: "300px",
                    }}
                >
                    <p style={{ marginBottom: "8px", fontWeight: "bold", fontSize: "13px" }}>
                        <strong>{displayServiceType}</strong>
                    </p>
                    <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "6px" }}>
                        <div 
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "4px 8px",
                                borderRadius: "4px",
                                backgroundColor: `${bgColor}15`,
                                borderLeft: `3px solid ${bgColor}`,
                            }}
                        >
                            <span style={{ fontSize: "12px", color: "#374151" }}>
                                {displayServiceType}
                            </span>
                            <span style={{ fontSize: "12px", fontWeight: "600", color: "#111827" }}>
                                {currency}{USAComma(exactLead.QuotedValue)}
                            </span>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    // const highestQuotedLead = topLeads.reduce((max, lead) => {
    //     return lead.QuotedValue > (max?.QuotedValue || 0) ? lead : max;
    // }, null);

    // const highestQuotedGroup = groupedData.reduce((max, item) => {
    //     return item.totalQuotedValue > max.totalQuotedValue ? item : max;
    // }, groupedData[0]);

    return (
        <div className="flex h-full min-h-0 w-full max-w-full gap-2 overflow-hidden sm:gap-3">
            <div
                className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-white px-2 sm:px-3"
                style={{
                    fontFamily: "Lora",
                    fontWeight: 600,
                    boxShadow: "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px"
                }}
            >
                <div className="flex h-full min-h-0 flex-col">
                    <div className="shrink-0 pt-0.5">
                        <div className="w-full flex flex-wrap md:flex-nowrap justify-between items-center">
                            <div className="w-full md:w-[70%]">
                                <h1 className="text-md font-semibold ">Top 10 Client</h1>
                            </div>
                            <div className="w-full md:w-[30%] flex items-center justify-end mt-2 md:mt-0">
                                <div className="show-potential-download lg:justify-end">
                                    <div className=" flex cursor-pointer items-center justify-between text-3xl sm:text-xl md:text-md">
                                        {probabilityTab === "table" && (
                                            <span title="Graph" className="rounded-full bg-[#3072f0] p-1">
                                                <ChartColumnDecreasing size={20} color="#ffffff"
                                                    onClick={() => setProbabilityTab("graph")}
                                                />
                                            </span>
                                        )}
                                        {probabilityTab === "graph" && (
                                            <span title="Table" className="rounded-full bg-[#3072f0] p-1 rotate-180">
                                                <TableOfContents size={20} color="#ffffff" onClick={() => setProbabilityTab("table")} />
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="show-potential-download lg:justify-end ">
                                    <div className=" inline-block relative px-3 mt-2">
                                        <div
                                            ref={dropdownRefclient}
                                            className={`flex items-center text-4xl sm:text-md md:text-xl lg:text-xl xl:text-2xl 2xl:text-4xl ${topLeads.length === 0
                                                ? "opacity-50 pointer-events-none"
                                                : ""
                                                } ${isDownloadViewOnly ? "opacity-60 pointer-events-none cursor-not-allowed" : ""}`}
                                        >
                                            <Download color={isDownloadViewOnly ? "#9ca3af" : "#0a0a0a"}
                                                onClick={toggleDropdown}
                                                className={isDownloadViewOnly ? "cursor-not-allowed" : "cursor-pointer"} />

                                            {isDropdownVisible && (
                                                <div className="absolute top-8 -left-0 shadow-lg z-50 bg-white text-base">
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownload({ target: { value: "pdf" } })
                                                        }
                                                    >
                                                        PDF
                                                    </div>
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownload({ target: { value: "excel" } })
                                                        }
                                                    >
                                                        Excel
                                                    </div>
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownload({ target: { value: "word" } })
                                                        }
                                                    >
                                                        Word
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="min-h-0 flex-1 flex lg:pt-3 xl:pt-3">
                        {probabilityTab === "table" ? (
                            topLeads.length > 0 ? (
                                <div className="w-full max-h-[9.5rem] overflow-auto custom-scroll border">
                                    <div className="w-full bg-[#E8E8E8] text-black text-xs font-bold flex sticky top-0">
                                        <div className="w-[50%] text-start px-1 py-1 border-r border-gray-400">
                                            <p>Client</p>
                                        </div>
                                        <div className="w-[50%] text-start px-1 py-1">
                                            <p>Amount ({currency})</p>
                                        </div>
                                    </div>

                                    <div className=" text-xs font-normal">
                                        {topLeads
                                            ?.slice() // clone so original state isn't mutated
                                            .sort((a, b) => b.QuotedValue - a.QuotedValue) // highest → lowest
                                            .map((item, index) => (<div className="w-full flex" key={index}>
                                                <div className="w-[50%] text-start border-b border-gray-300 border-r border-gray-400 px-1 py-1 break-words whitespace-normal">
                                                    {item.ClientName}
                                                </div>
                                                <div className="w-[50%] text-start  border-b border-gray-300 px-1 py-1">
                                                    {USAComma(item.QuotedValue)}
                                                </div>
                                            </div>
                                            ))}
                                    </div>
                                    <div className="w-full flex bg-[#E8E8E8] font-bold text-xs">
                                        <div className="w-[50%] text-start  px-1 py-1 border-r border-gray-400">
                                            <p>Total</p>
                                        </div>
                                        <div className="w-[50%]  text-start truncate  px-1 py-1">
                                            <p className="truncate">
                                                {USAComma(Number(topLeads
                                                    .reduce(
                                                        (total, item) =>
                                                            total + Number(item.QuotedValue || 0),
                                                        0
                                                    )))}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <p
                                            className={`py-0.5 text-[0.6rem] ${isDownloadViewOnly ? 'text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60' : 'text-[#0070c0] cursor-pointer'}`}
                                            onClick={AllClient}
                                        >
                                            See all*
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                                    <p className="noDataAvailable">
                                        No data available
                                    </p>{" "}
                                </div>
                            )
                        ) : topLeads.length > 0 ? (
                            <div className="w-full min-h-0 flex flex-1 flex-col justify-center">
                                <div className="w-full min-h-0 flex flex-1 items-center justify-between gap-8 py-1">
                                    <div className="relative z-10 flex-shrink-0 w-[min(480px,56%)] max-h-full p-1 flex flex-col items-center justify-center">
                                        <div className="relative z-20 mb-1 text-center">
                                            <div className="text-base font-bold text-[#1F2937] leading-none">10</div>
                                            <div className="mt-0.5 text-[12px] font-medium text-gray-400">clients</div>
                                        </div>
                                        <div className="relative z-10 w-full max-h-full aspect-square overflow-hidden">
                                        {(() => {
                                            const total = (data || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0) || 1;
                                            const pieRows = (data || []).map((r) => ({
                                                ...r,
                                                value: (Number(r?.originalValue || 0) / total) * 100,
                                            }));
                                            return (
                                                <>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie
                                                                data={pieRows}
                                                                cx="50%"
                                                                cy="50%"
                                                                innerRadius="50%"
                                                                outerRadius="99%"
                                                                fill="#8884d8"
                                                                dataKey="value"
                                                                minAngle={5}
                                                                paddingAngle={0.1}
                                                            >
                                                                {pieRows.map((entry, index) => (
                                                                    <Cell
                                                                        key={`cell-${index}`}
                                                                        fill={COLORS[index % COLORS.length]}
                                                                    />
                                                                ))}
                                                            </Pie>
                                                            <Tooltip content={<CustomTooltip />} />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    </div>

                                    {(() => {
                                        const total = (data || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0) || 1;
                                        const rows = (data || [])
                                            .map((r) => ({ ...r, pct: (Number(r?.originalValue || 0) / total) * 100 }))
                                            .sort((a, b) => (b.pct || 0) - (a.pct || 0));
                                        const topN = rows.slice(0, 4);
                                        const otherCount = Math.max(rows.length - topN.length, 0);
                                        const topSum = topN.reduce((t, r) => t + Number(r?.pct || 0), 0);
                                        const otherPct = Math.max(0, Math.round(100 - topSum));
                                        return (
                                            <div className="flex-1 min-w-0 pr-1">
                                                <div className="flex flex-col gap-3">
                                                    {topN.map((row, idx) => (
                                                        <div key={idx} className="flex w-full items-start gap-3 text-[12px] text-[#1F2937]">
                                                            <div className="flex flex-1 min-w-0 items-start gap-2">
                                                                <span
                                                                    className="h-2.5 w-2.5 rounded-full shrink-0"
                                                                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                                                                />
                                                                <span className="min-w-0 w-0 max-w-full flex-1 whitespace-normal break-words leading-snug">
                                                                    {row?.name || `Client ${idx + 1}`}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {otherCount > 0 && (
                                                        <div className="text-[11px] text-gray-400 whitespace-nowrap">
                                                            + {otherCount} others — {otherPct}%
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                                <p className="noDataAvailable">
                                    No data available
                                </p>{" "}
                            </div>
                        )}
                    </div>
                </div>
            </div>


            <div
                className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-white px-2 sm:px-3"
                style={{
                    fontFamily: "Lora",
                    fontWeight: 600,
                    boxShadow: "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px"
                }}
            >
                <div className="flex h-full min-h-0 flex-col">
                    <div className="shrink-0 pt-1">
                        <div className="flex flex-wrap justify-start items-center xl:flex xl:justify-between md:justify-between md:flex-nowrap ">
                            <div className="w-full md:w-[70%]">
                                <h1 className="text-md font-semibold">Revenue</h1>
                            </div>
                            <div className="md:flex lg:ml-8 lg:flex lg:items-center lg:justify-end xl:flex xl:justify-end">
                                <div className="show-potential-download lg:justify-end">
                                    <div className=" flex cursor-pointer items-center justify-between text-3xl sm:text-xl md:text-md">
                                        {probabilityTabRevenue === "table" && (
                                            <span title="Graph" className="rounded-full bg-[#3072f0] p-1">
                                                {/* <SignalCellularAltIcon
                                                    className="cursor-pointer"
                                                    onClick={() => setProbabilityTabRevenue("graph")}
                                                    fontSize="3rem"
                                                /> */}
                                                <ChartColumnDecreasing size={20} color="#ffffff"
                                                    onClick={() => setProbabilityTabRevenue("graph")}
                                                />
                                            </span>
                                        )}
                                        {probabilityTabRevenue === "graph" && (
                                            <span title="Table" className="rounded-full bg-[#3072f0] p-1 rotate-180">
                                                {/* <TableChartIcon
                                                    className="cursor-pointer"
                                                    onClick={() => setProbabilityTabRevenue("table")}
                                                    fontSize="3rem"
                                                /> */}
                                                <TableOfContents size={20}
                                                    color="#ffffff"
                                                    onClick={() => setProbabilityTabRevenue("table")} />

                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="show-potential-download lg:justify-end ">
                                    <div className=" inline-block relative px-3 mt-2">
                                        <div
                                            ref={popupRef}
                                            className={`flex items-center text-4xl sm:text-md md:text-xl lg:text-xl xl:text-2xl 2xl:text-4xl ${groupedData.length === 0
                                                ? "opacity-50 pointer-events-none"
                                                : ""
                                                } ${isDownloadViewOnly ? "opacity-60 pointer-events-none cursor-not-allowed" : ""}`}
                                        >

                                            <Download color={isDownloadViewOnly ? "#9ca3af" : "#0a0a0a"}
                                                onClick={toggleDropdownRevenue}
                                                className={isDownloadViewOnly ? "cursor-not-allowed" : "cursor-pointer"} />

                                            {isDropdownVisibleRevenue && (
                                                <div className="absolute top-8 -left-0 z-40 bg-white text-base shadow-lg">
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownloadRevenue({
                                                                target: { value: "pdf" },
                                                            })
                                                        }
                                                    >
                                                        PDF
                                                    </div>
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownloadRevenue({
                                                                target: { value: "excel" },
                                                            })
                                                        }
                                                    >
                                                        Excel
                                                    </div>
                                                    <div
                                                        className="px-2 py-2 cursor-pointer hover:bg-gray-100"
                                                        onClick={() =>
                                                            handleDownloadRevenue({
                                                                target: { value: "word" },
                                                            })
                                                        }
                                                    >
                                                        Word
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="min-h-0 flex-1 flex lg:pt-3 xl:pt-3">
                        {probabilityTabRevenue === "table" ? (
                            topLeads.length > 0 ? (
                                <div className="w-full max-h-[9.5rem] overflow-auto custom-scroll border">
                                        <div className="w-full bg-[#E8E8E8] text-black text-xs font-bold flex sticky top-0 z-[199]">
                                            <div className="w-[50%] text-start px-1 py-1 border-r border-gray-400">
                                                <p>Service Type</p>
                                            </div>
                                            <div className="w-[50%] text-start px-1 py-1">
                                                <p>Amount ({currency})</p>
                                            </div>
                                        </div>

                                        <div className="">
                                            {[...topLeads]  // copy so original array isn't mutated
                                                .sort((a, b) => b.QuotedValue - a.QuotedValue) // highest first
                                                .map((item, index) => {
                                                    const serviceTypes = item.TypeService || [];
                                                    const isMultiple = serviceTypes.length > 1;
                                                    const displayText = isMultiple ? "Multiple" : serviceTypes[0] || "";

                                                    return (
                                                        <div className="w-full flex text-xs font-normal" key={index}>
                                                            <div
                                                                className={`w-[50%] ${isMultiple ? "cursor-default" : "cursor-default"} border-gray-300 text-start border-b border-r border-gray-400 px-1 py-1 break-words whitespace-normal relative group`}
                                                                title={isMultiple ? serviceTypes.join(", ") : displayText} // 👈 comma-separated
                                                            >
                                                                {displayText}
                                                            </div>

                                                            {/* QuotedValue Column */}
                                                            <div className="w-[50%] text-start  border-gray-300 border-b px-1 py-1">
                                                                {USAComma(item.QuotedValue)}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>

                                        <div className="w-full bg-[#E8E8E8] flex font-bold text-xs">
                                            <div className="w-[50%] text-start  px-1 py-1 border-r border-gray-400">
                                                <p>Total</p>
                                            </div>
                                            <div className="w-[50%] text-start  truncate  px-1 py-1">
                                                <p>

                                                    {USAComma(Number(groupedData
                                                        .reduce(
                                                            (total, item) =>
                                                                total + Number(item.totalQuotedValue || 0),
                                                            0
                                                        )))}
                                                </p>
                                            </div>


                                        </div>

                                        <div>
                                            <p
                                                className={`py-0.5 text-[0.6rem] ${isDownloadViewOnly ? 'text-[#9ca3af] cursor-not-allowed pointer-events-none opacity-60' : 'text-[#0070c0] cursor-pointer'}`}
                                                onClick={AllClientServiceType}
                                            >
                                                See all*
                                            </p>
                                        </div>

                                </div>
                            ) : (
                                <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                                    <p className="noDataAvailable">
                                        No data available
                                    </p>{" "}
                                </div>
                            )
                        ) : groupedData.length > 0 ? (
                            <div className="w-full min-h-0 flex flex-1 flex-col justify-center">
                                <div className="w-full min-h-0 flex flex-1 items-center justify-between gap-8 py-1">
                                    <div className="relative z-10 flex-shrink-0 w-[min(480px,56%)] max-h-full p-1 flex flex-col items-center justify-center">
                                        {(() => {
                                            const total = (datarevenue || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0) || 1;
                                            const pieRows = (datarevenue || []).map((r) => ({
                                                ...r,
                                                value: (Number(r?.originalValue || 0) / total) * 100,
                                            }));
                                            const totalText = `${currency}${USAComma(
                                                (datarevenue || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0)
                                            )}`;
                                            return (
                                                <>
                                                    <div className="relative z-20 mb-1 text-center">
                                                        <div className="text-sm font-semibold text-[#1F2937] leading-none">
                                                            {totalText}
                                                        </div>
                                                        <div className="mt-0.5 text-[12px] font-medium text-gray-400">total</div>
                                                    </div>
                                                    <div className="relative z-10 w-full max-h-full aspect-square overflow-hidden">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie
                                                                data={pieRows}
                                                                cx="50%"
                                                                cy="50%"
                                                                innerRadius="50%"
                                                                outerRadius="99%"
                                                                fill="#8884d8"
                                                                dataKey="value"
                                                                minAngle={5}
                                                                paddingAngle={0.1}
                                                            >
                                                                {pieRows.map((entry, index) => (
                                                                    <Cell
                                                                        key={`cell-${index}`}
                                                                        fill={COLORSrevenue[index % COLORSrevenue.length]}
                                                                    />
                                                                ))}
                                                            </Pie>
                                                            <Tooltip content={<CustomTooltiprevenue />} />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>

                                    {(() => {
                                        const total = (datarevenue || []).reduce((s, r) => s + Number(r?.originalValue || 0), 0) || 1;
                                        const rows = (datarevenue || [])
                                            .map((r) => ({ ...r, pct: (Number(r?.originalValue || 0) / total) * 100 }))
                                            .sort((a, b) => (b.pct || 0) - (a.pct || 0));
                                        const topN = rows.slice(0, 4);
                                        const otherCount = Math.max(rows.length - topN.length, 0);
                                        const topSum = topN.reduce((t, r) => t + Number(r?.pct || 0), 0);
                                        const otherPct = Math.max(0, Math.round(100 - topSum));
                                        return (
                                            <div className="flex-1 min-w-0 pr-1">
                                                <div className="flex flex-col gap-3">
                                                    {topN.map((row, idx) => (
                                                        <div key={idx} className="flex w-full items-start gap-3 text-[12px] text-[#1F2937]">
                                                            <div className="flex flex-1 min-w-0 items-start gap-2">
                                                                <span
                                                                    className="h-2.5 w-2.5 rounded-full shrink-0"
                                                                    style={{ backgroundColor: COLORSrevenue[idx % COLORSrevenue.length] }}
                                                                />
                                                                <span className="min-w-0 w-0 max-w-full flex-1 whitespace-normal break-words leading-snug">
                                                                    {row?.name || `Group ${idx + 1}`}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {otherCount > 0 && (
                                                        <div className="text-[11px] text-gray-400 whitespace-nowrap">
                                                            + {otherCount} others — {otherPct}%
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                                <p className="noDataAvailable">
                                    No data available
                                </p>{" "}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>)
}

export default Sales